import React, { useState } from "react";
import { X, Upload, FileSpreadsheet, CheckCircle2, AlertCircle, RefreshCw, Layers, Folder, ShieldCheck } from "lucide-react";
import { categoryImporterService } from "../../services/categoryImporterService";

/**
 * CategoryImporterModal.js — Modal de Upload & Importação de Categorias iSet (.xlsx / .xml)
 *
 * Permite selecionar o arquivo `categorias-modelo.xlsx` ou exportações de e-commerce:
 * - Leitura e validação instantânea
 * - Pré-visualização da contagem de categorias lidas, raiz e subcategorias
 * - Execução com barra de progresso e relatório final idempotente
 */
export default function CategoryImporterModal({ isOpen, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [resultSummary, setResultSummary] = useState(null);

  if (!isOpen) return null;

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setLoading(true);
    setErrorMsg("");
    setResultSummary(null);

    try {
      const parsed = await categoryImporterService.parseFile(selectedFile);
      setParsedData(parsed);
    } catch (err) {
      console.error("Erro ao analisar planilha de categorias:", err);
      setErrorMsg("Não foi possível ler o arquivo: " + err.message);
      setParsedData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleStartImport = async () => {
    if (!parsedData || !parsedData.categories || parsedData.categories.length === 0) return;

    setImporting(true);
    setProgress(0);
    setErrorMsg("");

    try {
      const summary = await categoryImporterService.processImport({
        categories: parsedData.categories,
        onProgress: (p) => setProgress(p)
      });

      setResultSummary(summary);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Erro na importação de categorias:", err);
      setErrorMsg("Erro durante o processo de importação: " + err.message);
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setParsedData(null);
    setResultSummary(null);
    setErrorMsg("");
    setProgress(0);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* HEADER */}
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500 rounded-xl text-slate-900">
              <FileSpreadsheet size={20} />
            </div>
            <div>
              <h2 className="text-base font-bold tracking-tight">Importador de Categorias iSet (E-commerce)</h2>
              <p className="text-xs text-slate-400">Importação idempotente por `categories_id` e `parent_id` (.xlsx)</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* BODY */}
        <div className="p-6 space-y-4 text-xs overflow-y-auto flex-1">
          
          {errorMsg && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-2">
              <AlertCircle size={16} className="shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {!parsedData && !resultSummary && (
            <div className="border-2 border-dashed border-slate-300 hover:border-emerald-500 bg-slate-50 hover:bg-emerald-50/40 rounded-2xl p-8 text-center transition-all cursor-pointer relative group">
              <input
                type="file"
                accept=".xlsx, .xls, .csv"
                onChange={handleFileChange}
                disabled={loading}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div className="flex flex-col items-center gap-2">
                <div className="p-3 bg-white border border-slate-200 group-hover:border-emerald-300 rounded-2xl shadow-sm text-emerald-600 transition-colors">
                  {loading ? <RefreshCw size={28} className="animate-spin text-emerald-600" /> : <Upload size={28} />}
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800">
                    {loading ? "Lendo planilha de categorias..." : "Clique ou arraste o arquivo categorias-modelo.xlsx aqui"}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Suporta planilhas exportadas da iSet contendo colunas <code className="font-mono text-slate-600 font-bold">categories_id</code>, <code className="font-mono text-slate-600 font-bold">categories_name</code>, <code className="font-mono text-slate-600 font-bold">parent_id</code>.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* PRÉ-VISUALIZAÇÃO ANTES DA IMPORTAÇÃO */}
          {parsedData && !resultSummary && (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet size={24} className="text-emerald-600 shrink-0" />
                  <div>
                    <p className="font-bold text-emerald-950 text-sm">{file?.name}</p>
                    <p className="text-xs text-emerald-700 font-medium">
                      {parsedData.categories.length} categorias encontradas no arquivo
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleReset}
                  className="px-3 py-1.5 bg-white hover:bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-lg text-xs font-bold transition-all"
                >
                  Trocar Arquivo
                </button>
              </div>

              {/* AMOSTRA DE CATEGORIAS */}
              <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-2">
                <span className="font-bold text-slate-700 block text-[11px] uppercase tracking-wider">
                  Amostra das Categorias Reconhecidas:
                </span>
                <div className="max-h-48 overflow-y-auto divide-y divide-slate-100 font-mono text-[11px]">
                  {parsedData.categories.slice(0, 10).map((cat, idx) => (
                    <div key={idx} className="py-1.5 flex items-center justify-between text-slate-700">
                      <div className="flex items-center gap-2 truncate">
                        <Folder size={13} className="text-emerald-600 shrink-0" />
                        <span className="font-bold truncate">{cat.nome}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-600 font-bold">
                          ID: {cat.categories_id || "N/A"}
                        </span>
                        <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded font-bold">
                          Pai: {cat.parent_id || 0}
                        </span>
                      </div>
                    </div>
                  ))}
                  {parsedData.categories.length > 10 && (
                    <div className="py-1.5 text-center text-slate-400 font-sans italic">
                      + mais {parsedData.categories.length - 10} categorias...
                    </div>
                  )}
                </div>
              </div>

              {importing && (
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs font-bold text-slate-700">
                    <span>Importando categorias para o PocketBase...</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="bg-emerald-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* RELATÓRIO FINAL */}
          {resultSummary && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-3 text-emerald-900">
                <CheckCircle2 size={24} className="text-emerald-600 shrink-0" />
                <div>
                  <h3 className="font-bold text-sm">Importação de Categorias Concluída!</h3>
                  <p className="text-xs text-emerald-700">As categorias foram sincronizadas com o banco de dados de taxonomia.</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-white p-3 rounded-lg border border-emerald-200">
                  <span className="text-slate-500 font-bold text-[10px] block">PROCESSADAS</span>
                  <span className="text-lg font-bold font-mono text-slate-900">{resultSummary.processed}</span>
                </div>
                <div className="bg-white p-3 rounded-lg border border-emerald-200">
                  <span className="text-emerald-600 font-bold text-[10px] block">CRIADAS</span>
                  <span className="text-lg font-bold font-mono text-emerald-700">{resultSummary.created}</span>
                </div>
                <div className="bg-white p-3 rounded-lg border border-emerald-200">
                  <span className="text-blue-600 font-bold text-[10px] block">ATUALIZADAS</span>
                  <span className="text-lg font-bold font-mono text-blue-700">{resultSummary.updated}</span>
                </div>
              </div>

              {resultSummary.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
                  <span className="font-bold text-red-800 text-[11px] block">Avisos/Erros no Processo ({resultSummary.errors.length}):</span>
                  <div className="max-h-24 overflow-y-auto text-[10px] text-red-700 font-mono">
                    {resultSummary.errors.map((err, idx) => (
                      <div key={idx}>{err}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

        {/* FOOTER */}
        <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex items-center justify-between">
          <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:text-slate-800 font-semibold">
            {resultSummary ? "Fechar" : "Cancelar"}
          </button>

          {parsedData && !resultSummary && (
            <button
              onClick={handleStartImport}
              disabled={importing}
              className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold transition-all shadow-md flex items-center gap-1.5 disabled:opacity-50"
            >
              {importing ? (
                <>
                  <RefreshCw size={15} className="animate-spin" />
                  <span>Importando...</span>
                </>
              ) : (
                <>
                  <FileSpreadsheet size={15} />
                  <span>Confirmar Importação de Categorias</span>
                </>
              )}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
